import { useState } from 'react'

import { api, ErrorApi, type FilaCatalogo } from '../api'
import { DATO_TECNICO, leerDatoTecnico, type TipoEquipo } from './datoTecnicoItem'

interface Props {
  fila: FilaCatalogo
  puedeEditar: boolean
  alCambiar: () => void
  alFallar: (mensaje: string) => void
}

/**
 * El tipo de un item propio y su dato técnico. Los items viejos no tienen tipo y por eso no
 * salen en ninguna lista de equipos: aquí se les asigna, sin tener que crearlos de nuevo.
 */
export function TipoDelItem({ fila: f, puedeEditar, alCambiar, alFallar }: Props) {
  const conTipo = f.tipo && f.tipo !== 'otro' ? (f.tipo as TipoEquipo) : null
  const [editando, setEditando] = useState(false)
  const [tipo, setTipo] = useState<TipoEquipo | ''>(conTipo ?? '')
  const [dato, setDato] = useState('')
  const [ocupado, setOcupado] = useState(false)

  if (f.origen !== 'propio') return <span className="suave">—</span>

  const guardar = async () => {
    if (!tipo) return alFallar('Elige si es un panel, un inversor o una batería.')
    const tecnico = leerDatoTecnico(tipo, dato)
    if (typeof tecnico === 'string') return alFallar(tecnico)
    setOcupado(true)
    try {
      await api.cambiarItem(f.id!, { tipo, ...tecnico })
      setEditando(false)
      alCambiar()
    } catch (err) {
      alFallar(err instanceof ErrorApi ? err.message : 'No se pudo guardar.')
    } finally {
      setOcupado(false)
    }
  }

  if (!editando) {
    return (
      <span className="tipo-item">
        {conTipo ? `${DATO_TECNICO[conTipo].tipo} ${f.tecnico}` : <span className="insignia insignia-rechazado">Sin tipo: no sale en las listas</span>}
        {puedeEditar && (
          <button type="button" className="enlace pequeno" onClick={() => setEditando(true)}>
            {conTipo ? 'Cambiar' : 'Asignar tipo'}
          </button>
        )}
      </span>
    )
  }

  return (
    <span className="tipo-item editando">
      <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoEquipo | '')} aria-label="Tipo del item" disabled={ocupado}>
        <option value="">Elige…</option>
        <option value="panel">Panel</option>
        <option value="inversor">Inversor</option>
        <option value="bateria">Batería</option>
      </select>
      {tipo && (
        <input
          value={dato}
          onChange={(e) => setDato(e.target.value)}
          inputMode="decimal"
          aria-label={DATO_TECNICO[tipo].etiqueta}
          placeholder={DATO_TECNICO[tipo].etiqueta}
          disabled={ocupado}
        />
      )}
      <button type="button" className="secundario pequeno" onClick={guardar} disabled={ocupado}>
        {ocupado ? 'Guardando…' : 'Guardar'}
      </button>
      <button type="button" className="enlace pequeno" onClick={() => setEditando(false)} disabled={ocupado}>
        Cancelar
      </button>
    </span>
  )
}
